import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import VatRateHistoryTable from '../../../components/tables/VatRateHistoryTable';
import Button from '../../../components/common/Button';
import { useVatRateHistorySearch, useDeleteVatRateHistory } from '../../../hooks/useVatRateHistoryApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { VatRateHistoryDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const VatRateHistoryList: React.FC = () => {
  const { search } = useVatRateHistorySearch();
  const deleteMutation = useDeleteVatRateHistory();
  const [items, setItems] = useState<VatRateHistoryDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'VAT Rates', href: '#' },
  ];

  const fetchItems = async (page: number, size: number) => {
    setIsLoading(true);
    try {
      const response = await search({
        filters: {},
        page,
        pageSize: size,
        sortBy: 'EffectiveDate',
        sortDescending: true,
      });
      setItems(response?.results || []);
      setTotalRecords(response?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching VAT Rates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const handleDelete = async (dto: VatRateHistoryDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync(dto.id || '');
      await fetchItems(currentPage, pageSize);
    } catch (error) {
      console.error('Error deleting VatRateHistory:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/vatratehistories"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.VatRateHistory.View"
    >
      <Card
        title="Manage VAT Rates"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/vatratehistories/create')} />
        }
      >
        <VatRateHistoryTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/vatratehistories/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default VatRateHistoryList;
